import { forwardRef, type HTMLAttributes } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * Silueta de contenido que todavia no llego (06 §4.2).
 *
 * Solo para la PRIMERA carga de una superficie de datos: sin datos previos, se
 * dibuja la forma de lo que va a haber (filas de la altura de la densidad,
 * tarjetas, bloques de la ficha) y la pagina no salta cuando llegan. En un
 * refetch con datos en pantalla NO se usa: los datos viejos se quedan y la
 * lista baja a `opacity-60`. Botones, procesos y textos inline siguen con
 * `Cargando` / `Spinner`.
 *
 * El bloque es decorativo (`aria-hidden`); el estado lo anuncia la composicion
 * que lo contiene con `role="status"` y `aria-label="Cargando"`.
 */
export const Skeleton = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn("animate-pulse rounded-md bg-muted", className)}
      {...props}
    />
  ),
);
Skeleton.displayName = "Skeleton";

/** Anchos distintos por columna para que la silueta parezca una tabla y no una grilla. */
const ANCHOS = ["w-3/5", "w-2/5", "w-4/5", "w-1/3", "w-1/2", "w-1/4", "w-2/3"] as const;

function anchoDe(i: number): string {
  return ANCHOS[i % ANCHOS.length] ?? "w-1/2";
}

export interface SkeletonTablaProps {
  filas?: number;
  columnas?: number;
  /** Alto de fila de la densidad (MASTER §6): `normal` 40 px, `compacta` 32 px. */
  densidad?: "normal" | "compacta";
  className?: string;
}

/**
 * La silueta de una `<Table>`: la banda del encabezado y `filas` filas con la
 * altura de la densidad. Va en el lugar de la tabla, adentro de la misma
 * `Card padding="none"`, asi el borde y el ancho son los de la tabla real.
 */
export function SkeletonTabla({
  filas = 5,
  columnas = 5,
  densidad = "normal",
  className,
}: SkeletonTablaProps): JSX.Element {
  const altoFila = densidad === "compacta" ? "h-8" : "h-10";
  const indices = Array.from({ length: columnas }, (_, i) => i);
  return (
    <div role="status" aria-label="Cargando" className={cn("w-full text-sm", className)}>
      <div className="flex h-10 items-center gap-3 border-b border-border bg-muted px-3">
        {indices.map((i) => (
          <div key={i} className="flex-1">
            <Skeleton className={cn("h-3 bg-muted-foreground/20", anchoDe(i + 3))} />
          </div>
        ))}
      </div>
      {Array.from({ length: filas }, (_, f) => (
        <div
          key={f}
          className={cn(
            "flex items-center gap-3 border-b border-border px-3 last:border-b-0",
            altoFila,
          )}
        >
          {indices.map((i) => (
            <div key={i} className="flex-1">
              <Skeleton className={cn("h-3", anchoDe(i + f))} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export interface SkeletonTarjetasProps {
  n?: number;
  className?: string;
}

/**
 * La silueta de una lista de tarjetas-registro (06 §3.4): titulo y linea
 * secundaria a la izquierda, el importe arriba a la derecha.
 */
export function SkeletonTarjetas({ n = 3, className }: SkeletonTarjetasProps): JSX.Element {
  return (
    <div role="status" aria-label="Cargando" className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: n }, (_, i) => (
        <Card key={i} padding="compacto" className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-2">
            <Skeleton className={cn("h-3", anchoDe(i))} />
            <Skeleton className={cn("h-3", anchoDe(i + 5))} />
          </div>
          <Skeleton className="h-4 w-20 shrink-0" />
        </Card>
      ))}
    </div>
  );
}

/**
 * La silueta de una ficha (06 §6): el encabezado con badge y titulo, y dos
 * secciones con tres lineas cada una.
 */
export function SkeletonFicha({ className }: { className?: string }): JSX.Element {
  return (
    <div role="status" aria-label="Cargando" className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      {[0, 1].map((s) => (
        <Card key={s} padding="normal" className="space-y-3">
          <Skeleton className="h-3 w-1/4" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-2/3" />
        </Card>
      ))}
    </div>
  );
}
